import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/siteTranslations';
import TestimonialSlider from '../components/TestimonialSlider';
import MapSection from '../components/MapSection';
import SEO from '../components/SEO';

export default function Home() {
  const { lang } = useLanguage();
  const t = translations[lang];

  const primaryTreatments = [
    {
      title: lang === 'da' ? 'Sportsmassage' : 'Sports Massage',
      slug: 'sports-massage',
      desc: lang === 'da' ? 'Effektiv massage til motionister og atleter for hurtigere restitution og forebyggelse af skader.' : 'Effective massage for active individuals targeting muscle recovery and injury prevention.',
      image: 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/6f9e56e8-59c1-4c62-ab67-d52ab0ef33f1/sports-massage.jpg',
      time: '60 - 120 min',
      price: lang === 'da' ? 'Fra 700 DKK' : 'From 700 DKK'
    },
    {
      title: lang === 'da' ? 'Gravidmassage' : 'Pregnancy Massage',
      slug: 'pregnancy-massage',
      desc: lang === 'da' ? 'Nænsom og støttende massage tilpasset den gravides krop og behov gennem graviditeten.' : 'Nurturing massage tailored to the pregnant body and individual needs throughout pregnancy.',
      image: 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/6f9e56e8-59c1-4c62-ab67-d52ab0ef33f1/pregnancy-massage.jpg',
      time: '60 - 120 min',
      price: lang === 'da' ? 'Fra 700 DKK' : 'From 700 DKK'
    },
    {
      title: lang === 'da' ? 'Neuromuskulær massage' : 'Neuromuscular Massage',
      slug: 'neuromuscular-massage',
      desc: lang === 'da' ? 'Specialiseret behandling rettet mod nervesystem, triggerpunkter og kroniske muskelspændinger.' : 'Specialized therapy targeting the nervous system, trigger points, and chronic muscular tension.',
      image: 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/6f9e56e8-59c1-4c62-ab67-d52ab0ef33f1/neuromuscular-therapy-treatment.jpg',
      time: '60 - 120 min',
      price: lang === 'da' ? 'Fra 700 DKK' : 'From 700 DKK'
    }
  ];

  return (
    <div className="space-y-0">
      <SEO
        title={lang === 'da' ? 'Massage with Jette' : 'Massage with Jette – Professional Massage Copenhagen'}
        description={lang === 'da'
          ? 'Professionel massage i København med over 29 års erfaring. Individuelle behandlinger med fokus på balance, velvære og kroppens naturlige evne til at hele.'
          : 'Professional massage therapy in Copenhagen with over 29 years of experience. Tailored treatments focused on balance, well-being, and natural body healing.'
        }
      />
      
      {/* HERO SECTION */}
      <section className="relative pt-12 pb-20 md:py-24 bg-[#FAF7F2] border-b border-[#E8E0D7] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Hero Text */}
            <div className="lg:col-span-7 space-y-6 text-left">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#EEF0F8] border border-[#2E3191]/20 text-xs font-semibold text-[#2E3191]">
                <i className="fa-solid fa-sparkles text-[11px]" />
                <span>{t.hero.badge}</span>
              </div>

              <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#2C2825] leading-[1.12]">
                {t.hero.title}
              </h1>

              <p className="text-lg sm:text-xl text-[#6E6760] font-normal leading-relaxed max-w-2xl">
                {t.hero.subtitle}
              </p>

              <div className="pt-4 flex flex-wrap items-center gap-4">
                <a
                  href="https://massage-with-jette.planway.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 rounded-full bg-[#2E3191] text-white text-base font-semibold hover:bg-[#1E2063] shadow-md hover:shadow-lg transition-all active:scale-98 inline-flex items-center"
                >
                  {t.hero.ctaBook} <i className="fa-solid fa-arrow-right ml-2 text-xs" />
                </a>

                <Link
                  to="/om-jette"
                  className="px-7 py-4 rounded-full border border-[#E8E0D7] bg-white text-[#2C2825] text-base font-semibold hover:border-[#2E3191] hover:text-[#2E3191] transition-all"
                >
                  {t.hero.ctaAbout}
                </Link>
              </div>

              {/* Small Feature Badges */}
              <div className="pt-8 grid grid-cols-3 gap-4 border-t border-[#E8E0D7]/80 text-xs text-[#6E6760]">
                <div>
                  <strong className="block text-[#2C2825] font-serif-display text-lg">
                    {lang === 'da' ? '29+ år' : '29+ years'}
                  </strong>
                  <span>{lang === 'da' ? 'Erfaring med massage' : 'Experience with massage'}</span>
                </div>
                <div>
                  <strong className="block text-[#2C2825] font-serif-display text-lg">
                    {lang === 'da' ? 'RAB-godkendt' : 'RAB Approved'}
                  </strong>
                  <span>{lang === 'da' ? 'Anerkendt af sundhedsvæsenet' : 'Recognized by healthcare'}</span>
                </div>
                <a
                  href="https://www.saligdig.dk/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block hover:text-[#5B4636] transition-colors"
                >
                  <strong className="block text-[#2C2825] group-hover:text-[#5B4636] font-serif-display text-lg underline decoration-transparent group-hover:decoration-current transition-all">
                    {lang === 'da' ? 'Klinik hos Salig' : 'Clinic at Salig'}
                  </strong>
                  <span>{lang === 'da' ? 'Østerbro, Masnedøgade 20' : 'Østerbro, Masnedøgade 20'}</span>
                </a>
              </div>
            </div>

            {/* Hero Image Frame */}
            <div className="lg:col-span-5 relative">
              <div className="relative mx-auto max-w-md lg:max-w-none">
                <div className="aspect-4/5 rounded-3xl overflow-hidden border border-[#E8E0D7] shadow-xl relative group">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/6f9e56e8-59c1-4c62-ab67-d52ab0ef33f1/massage-with-jette-dsc09348_2.jpg"
                    alt="Jette Keis Hansen udfører massagebehandling"
                    className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                  
                  {/* Floating Quote Card */}
                  <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-md p-5 rounded-2xl border border-white/40 shadow-lg">
                    <p className="font-serif-display italic text-sm text-[#2C2825]">
                      "Healing is the return of the memory of wholeness."
                    </p>
                    <span className="text-[11px] font-semibold text-[#2E3191] block mt-1">
                      – Deepak Chopra
                    </span>
                  </div>
                </div>

                {/* Decorative background accent circle */}
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#2E3191]/10 rounded-full blur-2xl -z-10" />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* WHY JETTE SECTION */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-3">
            <span className="text-xs font-semibold uppercase tracking-widest text-[#2E3191] bg-[#EEF0F8] px-3.5 py-1 rounded-full border border-[#2E3191]/20 inline-block">
              {t.whyJette.tag}
            </span>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-bold text-[#2C2825]">
              {t.whyJette.title}
            </h2>
            <p className="text-base text-[#6E6760] leading-relaxed">
              {t.whyJette.intro}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {t.whyJette.points.map((point, idx) => (
              <div
                key={idx}
                className="p-8 rounded-2xl bg-[#FAF7F2] border border-[#E8E0D7] space-y-3 hover:border-[#2E3191]/50 transition-all duration-200"
              >
                <div className="w-12 h-12 rounded-xl bg-[#EEF0F8] text-[#2E3191] flex items-center justify-center text-xl mb-4">
                  <i className={`fa-solid ${point.icon}`} />
                </div>
                <h3 className="font-serif-display text-xl font-bold text-[#2C2825]">
                  {point.title}
                </h3>
                <p className="text-sm text-[#6E6760] leading-relaxed">
                  {point.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TREATMENTS OVERVIEW SECTION */}
      <section className="py-20 bg-[#FAF7F2] border-t border-[#E8E0D7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
            <div className="max-w-2xl space-y-3">
              <span className="text-xs font-semibold uppercase tracking-widest text-[#2E3191] bg-[#EEF0F8] px-3.5 py-1 rounded-full border border-[#2E3191]/20 inline-block">
                {t.treatmentsOverview.tag}
              </span>
              <h2 className="font-serif-display text-3xl sm:text-4xl font-bold text-[#2C2825]">
                {t.treatmentsOverview.title}
              </h2>
              <p className="text-base text-[#6E6760]">
                {t.treatmentsOverview.subtitle}
              </p>
            </div>

            <Link
              to="/behandlinger"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#2E3191] text-[#2E3191] text-sm font-semibold hover:bg-[#2E3191] hover:text-white transition-all shrink-0 self-start md:self-auto"
            >
              {t.treatmentsOverview.viewAll} <i className="fa-solid fa-arrow-right text-xs" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {primaryTreatments.map((item, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl border border-[#E8E0D7] overflow-hidden shadow-xs hover:shadow-md transition-all flex flex-col justify-between group"
              >
                <div>
                  <div className="aspect-16/9 sm:aspect-16/10 overflow-hidden bg-[#E8E0D7]">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6 sm:p-8 space-y-3">
                    <div className="flex items-center justify-between text-xs font-semibold text-[#2E3191]">
                      <span>{item.time}</span>
                      <span>{item.price}</span>
                    </div>
                    <h3 className="font-serif-display text-2xl font-bold text-[#2C2825]">
                      {item.title}
                    </h3>
                    <p className="text-sm text-[#6E6760] leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>

                <div className="px-6 sm:px-8 pb-6 sm:pb-8 pt-2">
                  <Link
                    to={`/behandlinger/${item.slug}`}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#2E3191] hover:text-[#1E2063] group-hover:underline"
                  >
                    {t.treatmentsOverview.readMore} <i className="fa-solid fa-chevron-right text-xs" />
                  </Link>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* OM JETTE INTRO SECTION */}
      <section className="py-20 bg-white border-t border-[#E8E0D7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-5 relative">
              <div className="aspect-4/5 rounded-3xl overflow-hidden border border-[#E8E0D7] shadow-lg">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/6f9e56e8-59c1-4c62-ab67-d52ab0ef33f1/massage-with-jette-dsc09361_2.jpg"
                  alt="Jette Keis Hansen i gang med behandling"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs font-semibold uppercase tracking-widest text-[#2E3191] bg-[#EEF0F8] px-3.5 py-1 rounded-full border border-[#2E3191]/20 inline-block">
                {t.aboutSnippet.tag}
              </span>

              <h2 className="font-serif-display text-3xl sm:text-4xl font-bold text-[#2C2825]">
                {t.aboutSnippet.title}
              </h2>

              <p className="text-base text-[#6E6760] leading-relaxed">
                {t.aboutSnippet.text1}
              </p>

              <p className="text-base text-[#6E6760] leading-relaxed">
                {t.aboutSnippet.text2}
              </p>

              {/* Deepak Chopra Quote Box */}
              <div className="p-6 rounded-2xl bg-[#FAF7F2] border-l-4 border-[#2E3191] space-y-2">
                <p className="font-serif-display italic text-lg sm:text-xl text-[#2C2825]">
                  {t.aboutSnippet.quote}
                </p>
                <span className="text-xs font-bold text-[#2E3191] block">
                  {t.aboutSnippet.quoteAuthor}
                </span>
              </div>

              <div>
                <Link
                  to="/om-jette"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#2E3191] text-white text-sm font-semibold hover:bg-[#1E2063] transition-all"
                >
                  {t.aboutSnippet.cta} <i className="fa-solid fa-arrow-right text-xs" />
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* PRISER OVERVIEW SECTION */}
      <section className="py-20 bg-[#FAF7F2] border-t border-[#E8E0D7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto mb-12 space-y-3">
            <span className="text-xs font-semibold uppercase tracking-widest text-[#2E3191] bg-[#EEF0F8] px-3.5 py-1 rounded-full border border-[#2E3191]/20 inline-block">
              {t.pricing.tag}
            </span>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-bold text-[#2C2825]">
              {t.pricing.title}
            </h2>
            <p className="text-base text-[#6E6760]">
              {t.pricing.subtitle}
            </p>
            <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-white border border-[#E8E0D7] text-xs font-semibold text-[#2E3191]">
              <i className="fa-solid fa-clock text-[#2E3191]" />
              <span>{t.pricing.timeBasedNote}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.pricing.times.map((item, idx) => (
              <div
                key={idx}
                className="bg-white p-7 rounded-2xl border border-[#E8E0D7] shadow-xs hover:border-[#2E3191] transition-all flex flex-col justify-between text-center space-y-6 group"
              >
                <div>
                  <span className="text-xs uppercase tracking-wider font-semibold text-[#2E3191] block mb-2">
                    {item.duration}
                  </span>
                  <div className="font-serif-display text-3xl sm:text-4xl font-bold text-[#2C2825] group-hover:text-[#2E3191] transition-colors">
                    {item.price}
                  </div>
                  <p className="text-xs text-[#6E6760] mt-3 leading-relaxed">
                    {item.desc}
                  </p>
                </div>

                <a
                  href="https://massage-with-jette.planway.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-2.5 rounded-xl border border-[#2E3191] text-[#2E3191] hover:bg-[#2E3191] hover:text-white text-xs sm:text-sm font-semibold transition-all block text-center"
                >
                  {lang === 'da' ? `Book (${item.duration})` : `Book (${item.duration})`}
                </a>
              </div>
            ))}
          </div>

          {/* Offers & RAB Banner */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="p-5 rounded-2xl bg-white border border-[#2E3191]/30 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#EEF0F8] text-[#2E3191] flex items-center justify-center text-lg shrink-0">
                <i className="fa-solid fa-tags" />
              </div>
              <div className="text-left">
                <span className="text-[11px] uppercase tracking-wider font-bold text-[#2E3191] block">
                  {lang === 'da' ? 'Klippekort rabat' : 'Package discount'}
                </span>
                <p className="text-sm font-semibold text-[#2C2825]">
                  {t.pricing.packageDeal.text}
                </p>
              </div>
            </div>

            <div className="p-5 rounded-2xl bg-white border border-[#E8E0D7] flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-[#EEF0F8] text-[#2E3191] flex items-center justify-center text-lg shrink-0">
                <i className="fa-solid fa-certificate" />
              </div>
              <div className="text-left">
                <span className="text-[11px] uppercase tracking-wider font-bold text-[#2E3191] block">
                  {t.pricing.rab.badge}
                </span>
                <p className="text-sm font-semibold text-[#2C2825]">
                  {t.pricing.rab.text}
                </p>
              </div>
            </div>
          </div>

          {/* Out-call note & link to full price page */}
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4 text-center">
            <div className="p-3 px-5 rounded-xl bg-[#EEF0F8] border border-[#2E3191]/20 text-xs text-[#2C2825] inline-flex items-center gap-2">
              <i className="fa-solid fa-car-side text-[#2E3191]" />
              <span>{t.pricing.outCallNote}</span>
            </div>

            <Link
              to="/priser"
              className="text-xs font-semibold text-[#2E3191] hover:underline inline-flex items-center gap-1"
            >
              {lang === 'da' ? 'Se alle priser & klippekort' : 'View all prices & packages'} →
            </Link>
          </div>

        </div>
      </section>

      {/* TESTIMONIALS SLIDER */}
      <TestimonialSlider />

      {/* MAP & CLINIC SECTION */}
      <MapSection />

    </div>
  );
}
